import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import LoadingOverlay from "react-loading-overlay";
import { Triangle } from "react-loader-spinner";
import { InputText } from "primereact/inputtext";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { handlePatchRequest } from "../../services/PatchTemplate";

const ManageContact = () => {

    const dispatch = useDispatch()

    const [isActive, setIsActive] = useState(false)
    const [allContacts, setAllContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [contactDetailDialog, setContactDetailDialog] = useState(false);

    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    //Get Contact List
    const getContactList = async () => {
        setIsActive(true);
        const response = await dispatch(handleGetRequest(`/contact/getAll`, true));
        if (response?.status === 'success' || response?.statusCode === 200) {
            setAllContacts(response?.data || []);
        }
        setIsActive(false);
    };

    useEffect(() => {
        getContactList();
    }, [])

    const dateTemplate = (rowData) => {
        if (rowData?.createdAt) {
            const date = new Date(rowData.createdAt);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return 'N/A';
    };

    const statusTemplate = (rowData) => {
        const isRead = rowData?.isRead || false;
        return (
            <Tag 
                value={isRead ? 'Read' : 'Unread'} 
                severity={isRead ? 'success' : 'warning'} 
            />
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <Button
                label="View Details"
                icon="pi pi-eye"
                className="p-button-sm"
                onClick={() => {
                    setSelectedContact(rowData);
                    setContactDetailDialog(true);
                    // Mark as read when viewing
                    if (!rowData?.isRead) {
                        handleMarkAsRead(rowData._id);
                    }
                }}
            />
        );
    };

    const handleMarkAsRead = async (id) => {
        await dispatch(handlePatchRequest({}, `/contact/${id}/read`, false, false));
        getContactList();
    };

    const contactDetailFooter = (
        <div>
            <Button
                label="Close"
                icon="pi pi-times"
                onClick={() => setContactDetailDialog(false)}
                className="p-button-text"
            />
        </div>
    );

    return (
        <>
            <div className="flex flex-column align-items-start md:flex-row  md:justify-content-between md:align-items-center ">
                <h4 className="mb-0" style={{ fontWeight: '600', letterSpacing: '0.5px', color: 'black' }}>
                    Contacts Management
                </h4>
                <div className="flex flex-column align-items-start  md:flex-row md:align-items-center">
                    <span className="p-input-icon-left mr-3 resp-margin my-3 md:my-0">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Search"
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            className="search-input"
                        />
                    </span>
                </div>
            </div>

            <div className="card mt-3">
                <LoadingOverlay
                    active={isActive}
                    spinner={<Triangle
                        height="120"
                        width="120"
                        color="#dc2626"
                        ariaLabel="triangle-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                    />}
                    styles={{
                        overlay: (base) => ({
                            ...base,
                            background: 'rgb(38 41 51 / 78%)',
                        })
                    }}
                >
                    <DataTable
                        responsive={true}
                        filters={filters}
                        globalFilterFields={[
                            "name",
                            "email",
                            "phone",
                            "subject",
                            "message",
                        ]}
                        responsiveLayout="scroll"
                        stripedRows
                        paginator
                        rows={20}
                        value={allContacts}
                        emptyMessage="No contacts found"
                        paginatorClassName="custom-paginator"
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                    >
                        <Column sortable field='name' body={(rowData) => rowData?.name || 'N/A'} header="Name"></Column>
                        <Column sortable field='email' body={(rowData) => rowData?.email || 'N/A'} header="Email"></Column>
                        <Column sortable field='phone' body={(rowData) => rowData?.phone || 'N/A'} header="Phone"></Column>
                        <Column sortable field='subject' body={(rowData) => rowData?.subject || 'N/A'} header="Subject"></Column>
                        <Column field='isRead' body={statusTemplate} header="Status"></Column>
                        <Column field='createdAt' body={dateTemplate} header="Date"></Column>
                        <Column header="Action" body={actionTemplate} />
                    </DataTable>
                </LoadingOverlay>
            </div>

            {/* Contact Detail Dialog */}
            <Dialog
                header="Contact Details"
                visible={contactDetailDialog}
                style={{ width: '90vw', maxWidth: '800px' }}
                footer={contactDetailFooter}
                onHide={() => setContactDetailDialog(false)}
                className="contact-detail-dialog"
            >
                {selectedContact && (
                    <div className="contact-detail-content">
                        {/* Contact Information Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Contact Information
                            </h5>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="info-item mb-3">
                                        <label>Name:</label>
                                        <span>{selectedContact.name || 'N/A'}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Email:</label>
                                        <span>{selectedContact.email || 'N/A'}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Phone:</label>
                                        <span>{selectedContact.phone || 'N/A'}</span>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="info-item mb-3">
                                        <label>Status:</label>
                                        <span>{statusTemplate(selectedContact)}</span>
                                    </div>
                                    <div className="info-item mb-3">
                                        <label>Submitted Date:</label>
                                        <span>{new Date(selectedContact.createdAt).toLocaleString()}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Subject Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Subject
                            </h5>
                            <div style={{ 
                                padding: '15px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '8px',
                                fontWeight: '500',
                                fontSize: '16px'
                            }}>
                                {selectedContact.subject || 'N/A'}
                            </div>
                        </div>

                        {/* Message Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Message
                            </h5>
                            <div style={{ 
                                padding: '15px', 
                                backgroundColor: '#f8f9fa', 
                                borderRadius: '8px',
                                whiteSpace: 'pre-wrap',
                                maxHeight: '400px',
                                overflowY: 'auto',
                                lineHeight: '1.6'
                            }}>
                                {selectedContact.message || 'N/A'}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            <style>{`
                .contact-detail-content {
                    padding: 10px 0;
                }
                .info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .info-item label {
                    font-weight: 600;
                    color: #333;
                    font-size: 13px;
                    margin-bottom: 4px;
                }
                .info-item span {
                    color: #666;
                    font-size: 14px;
                }
                .contact-detail-dialog .p-dialog-content {
                    padding: 1.5rem;
                }
            `}</style>
        </>
    );
};

export default ManageContact;
